//-- angular core
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, tap, throwError,} from 'rxjs';

//-- app
import {ResponseXBVO, TenantURL, UserModel} from "../base.model";
import {BaseService} from "../base.service";
import {Router} from "@angular/router";
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {environment} from "../../../../environments/environment";
import {catchError, map} from "rxjs/operators";
import {MessageService} from "primeng/api";
import {JwtHelperService} from "@auth0/angular-jwt";
import {DateUtilsBs} from "../utils/dateUtilsBs";
import { BsaAuthSessionXBVO, BsaAuthTokenXBVO } from '../../model/dto.models.imported';

@Injectable()
export class AuthService extends BaseService {

    private incomingMessagesSubject = new BehaviorSubject<number>(0);
    public readonly incomingMessages$ = this.incomingMessagesSubject.asObservable();

    static TAG_AUTH_TOKEN: string = "auth_token_session";
    static TAG_USERS: string = "auth_token_history";
    static HTTP_FORM_URLENCODED = new HttpHeaders().append('Content-Type', `application/x-www-form-urlencoded`);

    /**
     * Constructor.
     */
    constructor(private httpClient: HttpClient,
                private router: Router,
                private jwtHelper: JwtHelperService,
                private messageService: MessageService
    ) {
        super();
        const pSession = this.findCurrentSession();
        this.incomingMessagesSubject.next(pSession && pSession.incomingMessages ? pSession.incomingMessages : 0);
    }

    /**
     * Calls REST.
     */
    public auth(aLogin: string, aPasswd: string): Observable<BsaAuthSessionXBVO> {
        aPasswd = encodeURIComponent(aPasswd);
        let pFormBody = `login=${aLogin}&pwd=${aPasswd}`;

        // determine API URL by login (108, 109, 400 etc)
        let pURL = this.resolve_URL_Login(aLogin);

        return this.httpClient
            .post<BsaAuthSessionXBVO>(pURL + "/auth", pFormBody, {observe: 'response', headers: AuthService.HTTP_FORM_URLENCODED})
            .pipe(
                tap(aResponse => this.saveCurrentSession(aResponse.body)),
                catchError(aErrorResponse => {
                    return this.onError(aErrorResponse)
                }),
                map(aResponse => aResponse.body)
            );
    }


    /**
     * Logout.
     */
    public logout() {
        this.removeAuthToken();
        this.router.navigate(['/login']);
    }


    /**
     * Calls REST.
     */
    public refreshToken(refreshToken: string): Observable<BsaAuthTokenXBVO> {
        let pFormBody = `refreshToken=${refreshToken}`;
        return this.httpClient
            .post<BsaAuthTokenXBVO>(this.resolve_REST_URL_By_Token() + "/auth_refresh_token", pFormBody, {observe: 'response', headers: AuthService.HTTP_FORM_URLENCODED})
            .pipe(map(aResponse => aResponse.body));
    }

    /**
     * Saves.
     * @param aToken
     */
    public switchUser(aToken?: BsaAuthSessionXBVO) {
        this.saveCurrentSession(aToken);
    }


    /**
     * Checks.
     */
    public findCurrentSession(): BsaAuthSessionXBVO {
        let pJSON = localStorage.getItem(AuthService.TAG_AUTH_TOKEN);
        if (!pJSON) {
            return null;
        }
        let pXBVO: BsaAuthSessionXBVO = JSON.parse(pJSON);
        return pXBVO;
    }

    /**
     * Saves.
     * @param aAuthToken
     */
    public saveCurrentSession(aAuthToken?: BsaAuthSessionXBVO) {
        this.logd("[JWT] Save token:" + AuthService.parseTokenInfo(aAuthToken.token));

        if (aAuthToken) {
            localStorage.setItem(AuthService.TAG_AUTH_TOKEN, JSON.stringify(aAuthToken));
            this.incomingMessagesSubject.next(aAuthToken.incomingMessages || 0);
            // update User history
            this.saveUserHistory(aAuthToken);
        }
    }

    /** Updates the locally stored unread-message count and notifies navigation badges. */
    public updateIncomingMessages(aCount: number): void {
        const pSession = this.findCurrentSession();
        const pCount = Math.max(0, Number(aCount) || 0);
        if (!pSession) {
            this.incomingMessagesSubject.next(0);
            return;
        }
        pSession.incomingMessages = pCount;
        localStorage.setItem(AuthService.TAG_AUTH_TOKEN, JSON.stringify(pSession));
        this.incomingMessagesSubject.next(pCount);
    }

    /**
     * Refreshes session.
     * @param aRefreshSession
     */
    public refreshCurrentSession(aRefreshSession: BsaAuthTokenXBVO) {

        // find current
        let pCurrent = this.findCurrentSession();

        // update
        pCurrent.token = aRefreshSession.token;
        pCurrent.refreshToken = aRefreshSession.refreshToken;
        pCurrent.faObjectName = aRefreshSession.faObjectName;
        pCurrent.faObjectNr = aRefreshSession.faObjectNr;
        pCurrent.tgRfid = aRefreshSession.tgRfid;
        pCurrent.alertNotification = aRefreshSession.alertNotification;
        pCurrent.incomingMessages = aRefreshSession.incomingMessages;
        pCurrent.prepaidActive = aRefreshSession.prepaidActive;
        this.saveCurrentSession(pCurrent);
    }

    /**
     * Removes.
     */
    removeAuthToken() {
        localStorage.removeItem(AuthService.TAG_AUTH_TOKEN);
        this.incomingMessagesSubject.next(0);
    }

    /**
     * Checks.
     */
    public getToken(): string {
        let pXBVO = this.findCurrentSession();
        return pXBVO != null ? pXBVO.token : null;
    }


    /**
     * Checks.
     */
    public getRefreshToken(): string {
        let pXBVO = this.findCurrentSession();
        return pXBVO != null ? pXBVO.refreshToken : null;
    }

    /**
     * Checks.
     */
    public isTokenActive(): boolean {
        let pToken = this.getToken();
        return pToken == null ? false :  !this.jwtHelper.isTokenExpired(pToken, 60);
    }

    // ##########################################
    // # User history
    // ##########################################

    /**
     * Checks.
     */
    public findUserHistory(): UserModel[] {
        let pJSON = localStorage.getItem(AuthService.TAG_USERS);
        return (pJSON != null ? JSON.parse(pJSON) : null);
    }

    /**
     * Saves.
     * @param aAuthToken
     */
    public saveUserHistory(aAuthToken?: BsaAuthSessionXBVO) {
        if (aAuthToken == null) {
            return;
        }

        // add | update User
        let pUsers: UserModel[] = this.findUserHistory();
        if (pUsers) {
            let pExisting = pUsers.find(aUser => aUser.authXBVO.cpNr === aAuthToken.cpNr);
            if (pExisting != null) {
                // update existing
                pExisting.lastUsed = new Date();
                pExisting.authXBVO = aAuthToken;
            } else {
                // add new
                pUsers.push(UserModel.create(aAuthToken))
            }
        } else {
            // add new
            pUsers = [UserModel.create(aAuthToken)];
        }

        // sort by lastUsed
        pUsers.sort((a, b) => {
            if (a.lastUsed == b.lastUsed) {
                return 0;
            }
            return a.lastUsed > b.lastUsed ? -1 : 1;
        });

        // cleanup data
        let pDirtyUsers = pUsers.filter(aUser => aUser.authXBVO == null || aUser.authXBVO == undefined || aUser.authXBVO.cpNr == null || aUser.authXBVO.cpNr == undefined);
        pDirtyUsers.forEach(aUser => {
            pUsers.splice(pUsers.indexOf(aUser), 1);
        });

        // persist
        localStorage.setItem(AuthService.TAG_USERS, JSON.stringify(pUsers));
    }

    /**
     * Saves.
     * @param aTokenSession
     */
    public removeUserHistory(aUser: UserModel) {
        if (aUser == null || aUser.authXBVO.cpNr == null) {
            return;
        }
        let pUsers: UserModel[] = this.findUserHistory();
        if (pUsers) {
            let pExisting = pUsers.find(aUS => aUS.authXBVO.cpNr === aUser.authXBVO.cpNr);
            if (pExisting != null) {
                pUsers.splice(pUsers.indexOf(pExisting), 1);
            }
        }
        // persist
        localStorage.setItem(AuthService.TAG_USERS, JSON.stringify(pUsers));
    }


    /**
     * Error handler
     */
    public onError(aErrorResponse: HttpErrorResponse): Observable<any> {
        this.logi("[RestService.onError] " + JSON.stringify(aErrorResponse));
        if (aErrorResponse.status == 422) {
            var pResponseXBVO: ResponseXBVO = aErrorResponse.error;
            pResponseXBVO.httpStatus = aErrorResponse.status;
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Fehler', detail: this.i18n.v(pResponseXBVO.errorDescription), key: "toast-messages"});
            return throwError("[" + aErrorResponse.status + "] " + aErrorResponse.error);

        } else {
            console.log('publishing new Message... ');
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Fehler', detail: aErrorResponse.error + " " + aErrorResponse.message, key: "toast-messages"});
            return throwError("[" + aErrorResponse.status + "] " + aErrorResponse.error);
        }
    }


    /**
     * Debug token.
     */
    private debugToken(aToken: string) {
        if (!environment.production) {
            const helper = new JwtHelperService();
            const decodedToken = helper.decodeToken(aToken);
            this.logd("[" + helper.getTokenExpirationDate(aToken) + ": " + JSON.stringify(decodedToken));
        }
    }

    /**
     * Debugs.
     * @param aToken
     * @param aRequest
     * @private
     */
    static parseTokenInfo(aToken: string): string {
        const helper = new JwtHelperService();
        const pDecoded = helper.decodeToken(aToken);
        const pExpiresAt = new Date(0);
        pExpiresAt.setUTCSeconds(pDecoded.exp);
        return DateUtilsBs.format(pExpiresAt, "yyyy-MM-dd HH:mm:ss") + ", is expired: " + helper.isTokenExpired(aToken) + ", " + JSON.stringify(pDecoded);
    }

    /**
     * Server URLs for Authentication process.
     */
    public resolve_REST_URLs_By_Environment(): TenantURL[] {
        let pSingleURL = !environment.BASE_API_URL.includes("{");
        if (pSingleURL) {
            return [TenantURL.create(environment.TENANT_NR, environment.BASE_API_URL)];
        } else {
            let pTenantAPIs: TenantURL[] = JSON.parse(environment.BASE_API_URL);
            return pTenantAPIs;
        }
    }


    /**
     * Server API-URL for Session specific Tenant.
     */
    public resolve_REST_URL_By_Token(): string {
        let pAuthXBVO = this.findCurrentSession();

        // single URL
        let pEnvironmentURLs = this.resolve_REST_URLs_By_Environment();
        if (pEnvironmentURLs.length == 1) {
            return pEnvironmentURLs[0].url;
        }

        // match Token-URL
        let pURL = pEnvironmentURLs.find(aTenantURL => aTenantURL.tnr === pAuthXBVO.tenantNr).url;
        this.logi("SESSION API-URL: " + pURL);
        return pURL;
    }

    /**
     * Filter by login.
     * @param aLogin
     * @private
     */
    public resolve_URL_Login(aLogin: string): string {
        // determine Tenant by login (108, 109, 400 etc)
        let pTenantURLs = this.resolve_REST_URLs_By_Environment();

        // single tenant
        if (pTenantURLs.length == 1) {
            return pTenantURLs[0].url;
        }

        // multiple tenants
        let pTenantURL = pTenantURLs.find(aTenantURL => {
            // parse Env prefixes
            let pEnvPrefixes: string[] = aTenantURL.prefix.split(",");
            return pEnvPrefixes.find(aEnvPrefix => aLogin.startsWith(aEnvPrefix)) != null;
        });
        let pFinal = pTenantURL != null ? pTenantURL : pTenantURLs[0]; // fallback
        this.logi("[" + aLogin + "] RESOLVED TENANT|URL by LOGIN:  " + pFinal.tnr + "|" + pFinal.url);
        return pFinal.url;
    }
}
